import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Plus, Trash2 } from 'lucide-react';
import Text from '../../components/Text';
import Header from '../../components/Header';
import Actions from '../../components/Actions';
import Title from '../../components/Title';
import Line from '../../components/Line';
import List from '../../components/List';
import Tabs from '../../components/Tabs';
import { nameWithDefault } from '../../utils/helpers';
import Textarea from '../../components/Textarea';
import TabsCut from '../../components/TabsCut';
import TabsList from '../../components/TabsList';
import TabsListTitle from '../../components/TabsListTitle';
import TabsListItem from '../../components/TabsListItem';
import { REVENUE_MAX_COUNT } from '../../utils/const';
import Button from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal';
import SvgIcon from '../../components/SvgIcon';
import MobileMenuHeader from '../../components/MobileMenuHeader';

const fields = ['name', 'description'];
const icons = ['complexArrow', 'text'];
const iconsComponents = icons.map((e) => <SvgIcon name={e} />);

export default function RevenueModel(props) {
  const {
    nextClickHandler,
    menuItems,
    selectComponent,
    componentIndex,
    rules,
    backPath,
    locales,
    locales: {
      caseStudies,
      common,
      tipCustomerSegment,
      costRevenue: {
        name,
        revenueModel: { header, title, text, list, tabs },
      },
    },
  } = props;
  const { collection } = useSelector((state) => state.revenue);
  const {
    revenue: { addRevenue, updateRevenue, removeRevenue },
  } = useDispatch();
  const [opened, setOpened] = useState(false);

  return (
    <section className="bmc-page" aria-label={header}>
      <Header
        title={header}
        subtitle={name}
        icon="icon-revenue"
        locales={locales}
        componentIndex={componentIndex}
        className="bmc-page-header--violet"
        tipLocales={tipCustomerSegment}
        menuItems={menuItems}
        onMenuItemClick={selectComponent}
        rules={rules}
        backPath={backPath}
        mobileMenuHeader={
          <MobileMenuHeader icon="FinanceCanvas" title={name} color="violet" />
        }
      />

      <Text>
        {[
          text[0],
          text[1],
          text[2],
          text[3],
          text[4],
          text[5],
          text[6],
          text[7],
          text[8],
          text[9],
        ]}
      </Text>

      <Title text={title[0]} align="center" />

      <List items={list} type="horizontal" icons={icons} />

      <Line />

      <h2 className="bmc-page-title">
        <span className="bmc-page-title--violet">{title[1]}</span> {title[2]}
      </h2>

      <Text align="center">{[text[10]]}</Text>

      <Tabs
        tabs={[common.yourStrategy, common.caseStudy]}
        withBlur
        className="margin-top-40 bmc-page-tabs--violet"
      >
        {[
          <div key="strategy">
            {collection.map((item, index) => (
              <TabsList
                key={item.id}
                formStyle
                divider={index < REVENUE_MAX_COUNT - 1}
              >
                <TabsListTitle className="flex flex-row justify-between items-center">
                  {nameWithDefault(item.name, name, index + 1)}
                  {collection.length > 1 && (
                    <ConfirmationModal
                      className="ml-3 hover:bg-muted-lighter/60 text-muted"
                      body={locales.common.deleteConfirmation}
                      confirmText="Delete"
                      buttonAriaLabel="Delete"
                      onConfirm={() => removeRevenue({ index, id: item.id })}
                    >
                      <Trash2 className="w-6 min-w-6 h-auto" />
                    </ConfirmationModal>
                  )}
                </TabsListTitle>

                {fields.map((field, i) => (
                  <TabsListItem
                    divider={false}
                    className={i !== fields.length - 1 && 'mb-5'}
                    title={tabs[field]}
                    subtitle={tabs[`${field}Sub`]}
                    key={i}
                    icon={iconsComponents[i]}
                    text={
                      // eslint-disable-next-line
                      <Textarea
                        delay={700}
                        placeholder={tabs[field]}
                        value={item[field]}
                        onChange={(event) =>
                          updateRevenue({
                            name: field,
                            value: event.target.value,
                            index,
                            id: item.id,
                          })
                        }
                      />
                    }
                  />
                ))}
              </TabsList>
            ))}

            {collection.length < REVENUE_MAX_COUNT && (
              <div className="bmc-page-tabs-content-actions margin-top-40">
                <Button
                  variant="outline"
                  onClick={addRevenue}
                  className="bg-transparent hover:bg-secondary/50 border-secondary/50 text-secondary hover:text-secondary hover:border-secondary w-full"
                >
                  <Plus className="w-7 h-auto" />
                  {tabs.addNewRevenueModel}
                </Button>
              </div>
            )}
          </div>,

          <Tabs
            key="tabs"
            tabs={caseStudies.map((e) => e.name)}
            className="bmc-page-tabs--black bmc-page-tabs--inner"
          >
            {caseStudies.map((item, index) => (
              <div key={index}>
                <TabsCut
                  opened={opened}
                  setOpened={setOpened}
                  caseStudy={item}
                />

                {item.revenues.map((revenue, i) => (
                  <TabsList
                    key={i}
                    formStyle
                    preview
                    divider={i < item.revenues.length - 1}
                  >
                    <TabsListTitle>
                      {tabs.title} {i + 1}
                    </TabsListTitle>

                    {fields.map((field, ii) => (
                      <TabsListItem
                        divider={false}
                        className={ii < fields.length - 1 && 'mb-5'}
                        key={ii}
                        title={tabs[field]}
                        text={revenue[field]}
                        icon={iconsComponents[ii]}
                      />
                    ))}
                  </TabsList>
                ))}
              </div>
            ))}
          </Tabs>,
        ]}
      </Tabs>

      <Actions nextClickHandler={nextClickHandler} buttonName={common.next} />
    </section>
  );
}
